import React, { useState, useRef } from 'react';
import { expenseService } from '../services/expenseService';

function ImportCSV({ onImport, onCancel }) {
  const [csvData, setCsvData] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [template, setTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('default');
  const [useCsvCategories, setUseCsvCategories] = useState(false);
  const fileInputRef = useRef(null);

  const templateOptions = [
    {
      value: 'default',
      label: 'Default Template',
      fileInfo: 'Standard CSV format with basic expense fields',
      requiredColumns: [
        { name: 'description', desc: 'Brief description of the expense' },
        { name: 'amount', desc: 'Numeric value (can include $ and commas)' },
        { name: 'date', desc: 'Date in YYYY-MM-DD, MM/DD/YYYY, or MM-DD-YYYY format' }
      ],
      optionalColumns: [
        { name: 'category', desc: 'Expense category (will create new ones if needed)' }
      ]
    },
    {
      value: 'PARTNERS',
      label: 'Partners Bank New England',
      fileInfo: 'Export from Partners online banking - includes transaction details and account info',
      requiredColumns: [
        { name: 'Account Name', desc: '(ignored) Account pertaining to the transaction' },
        { name: 'Processed Date', desc: 'Transaction date from bank export' },
        { name: 'Description', desc: 'Bank transaction description' },
        { name: 'Check Number', desc: '(ignored) if a check' },
        { name: 'Credit or Debit', desc: 'Only DEBIT will be consumed' },
        { name: 'Amount', desc: 'Transaction amount (negative for debits)' }
      ],
      optionalColumns: [
      ]
    },
    {
      value: 'SVCU',
      label: 'Saco Valley Credit Union',
      fileInfo: 'Export from SVCU online banking - includes transaction details and account info',
      requiredColumns: [
        { name: 'Account', desc: '(ignored) Account pertaining to the transaction' },
        { name: 'Date', desc: 'Transaction date from bank export' },
        { name: 'Description', desc: 'Bank transaction description' },
        { name: 'Note', desc: '(ignored) Note you may have put' },
        { name: 'Check #', desc: '(ignored) if a check' },
        { name: 'Amount', desc: 'Transaction amount (negative for debits)' },
        { name: 'Account balance', desc: '(ignored) if a check' },
      ],
      optionalColumns: [
      ]
    },
    {
      value: 'CHASE',
      label: 'Chase Credit Card',
      fileInfo: 'Chase credit card statement export - includes merchant and category data',
      requiredColumns: [
        { name: 'Transaction Date', desc: '(ignored) Date of the transaction' },
        { name: 'Post Date', desc: 'Post Date of the transaction' },
        { name: 'Description', desc: 'Merchant or transaction description' },
        { name: 'Category', desc: 'Assigned category CHASE gives for the transaction' },
        { name: 'Type', desc: '(ignored) Marks the transaction as a charge, debit etc.' },
        { name: 'Amount', desc: 'Transaction amount' },
        { name: 'Memo', desc: '(ignored) Notes the vendor may have added' }
      ],
      optionalColumns: [
      ]
    },
    {
      value: 'DISCOVER',
      label: 'Discover Credit Card',
      fileInfo: 'Discover credit card statement export - includes category data',
      requiredColumns: [
        { name: 'Transaction Date', desc: '(ignored) Date of the transaction' },
        { name: 'Post Date', desc: 'Post Date of the transaction' },
        { name: 'Description', desc: 'Merchant or transaction description' },
        { name: 'Amount', desc: 'Transaction amount' },
        { name: 'Category', desc: 'Assigned category Discover gives for the transaction' }
      ],
      optionalColumns: [
      ]
    }
  ];

  // Get current template data
  const getCurrentTemplate = () => {
    return templateOptions.find(option => option.value === selectedTemplate) || templateOptions[0];
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setCsvData(e.target.result);
      setError('');
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  };

  const handleTextareaChange = (event) => {
    setCsvData(event.target.value);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!csvData.trim()) {
      setError('Please provide CSV data');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const currentTemplate = getCurrentTemplate();
      const result = await expenseService.importCSV(csvData, selectedTemplate, currentTemplate.label, useCsvCategories);
      
      let successMessage = `Successfully imported ${result.importedCount} expenses`;
      if (result.keywordMatchedCount > 0) {
        successMessage += ` (${result.keywordMatchedCount} auto-categorized by keywords)`;
      }
      if (result.newCategoriesCount > 0) {
        successMessage += ` and created ${result.newCategoriesCount} new categories`;
      }
      
      setSuccess(successMessage);

      // Clear the form
      setCsvData('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Call the parent callback after a short delay to show success message
      setTimeout(() => {
        onImport(result);
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      setLoadingTemplate(true);
      const templateData = await expenseService.getImportTemplate(selectedTemplate);
      setTemplate(templateData);

      // Create and download the template file
      const blob = new Blob([templateData.template], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expense_template_${selectedTemplate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download template');
    } finally {
      setLoadingTemplate(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      backdropFilter: 'blur(4px)'
    }}>
      <div className="card" style={{
        width: '600px',
        margin: 0,
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <h3 style={{
          color: '#ff6b35',
          marginBottom: '24px',
          fontSize: '1.5rem',
          fontWeight: '600',
          textAlign: 'center'
        }}>
          Import Expenses from CSV
        </h3>

        {error && (
          <div style={{
            color: '#ff4757',
            backgroundColor: 'rgba(255, 71, 87, 0.1)',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid rgba(255, 71, 87, 0.3)'
          }}>
            {error}
          </div>
        )}

        {success && (
          <div className="success-message" style={{ marginBottom: '20px' }}>
            {success}
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <h4 style={{ color: '#f0f0f0', margin: '0' }}>CSV Format</h4>
            {/* <button
              type="button"
              onClick={downloadTemplate}
              disabled={loadingTemplate}
              style={{
                background: 'rgba(255, 107, 53, 0.2)',
                border: '1px solid #ff6b35',
                color: '#ff6b35',
                padding: '6px 12px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: '500'
              }}
            >
              {loadingTemplate ? 'Loading...' : '📥 Download Template'}
            </button> */}
          </div>

          <div style={{
            background: 'rgba(255, 107, 53, 0.05)',
            border: '1px solid #404040',
            borderRadius: '8px',
            padding: '16px',
            fontSize: '14px',
            color: '#b0b0b0'
          }}>
            <p style={{ margin: '0 0 12px 0', fontWeight: '600', color: '#ff6b35' }}>
              Please note that income/deposites will be ignored.
            </p>
          </div>

          <div className="form-group" style={{ marginTop: '16px', marginBottom: '16px' }}>
            <label htmlFor="template">Template Type</label>
            <select
              id="template"
              name="template"
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
            >
              {templateOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginTop: '16px', marginBottom: '16px' }}>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#e0e0e0'
            }}>
              <input
                type="checkbox"
                checked={useCsvCategories}
                onChange={(e) => setUseCsvCategories(e.target.checked)}
                style={{
                  width: '16px',
                  height: '16px',
                  accentColor: '#ff6b35'
                }}
              />
              Use categories from CSV file
            </label>
            <small style={{ 
              color: '#b0b0b0', 
              fontSize: '12px', 
              marginTop: '4px', 
              display: 'block',
              marginLeft: '24px'
            }}>
              {useCsvCategories 
                ? 'Categories from the CSV will be used and new ones created if needed'
                : 'All expenses will be categorized as "Unknown" regardless of CSV categories'
              }
            </small>
          </div>

          <div style={{
            background: 'rgba(255, 107, 53, 0.05)',
            border: '1px solid #404040',
            borderRadius: '8px',
            padding: '16px',
            fontSize: '14px',
            color: '#b0b0b0'
          }}>
            <p style={{ margin: '0 0 12px 0', fontWeight: '600', color: '#ff6b35' }}>
              {getCurrentTemplate().fileInfo}
            </p>

            <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Required columns:</p>
            <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px' }}>
              {getCurrentTemplate().requiredColumns.map((col, index) => (
                <li key={index}>
                  <strong>{col.name}</strong> - {col.desc}
                </li>
              ))}
            </ul>

            {getCurrentTemplate().optionalColumns && getCurrentTemplate().optionalColumns.length > 0 && (
              <>
                <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>Optional columns:</p>
                <ul style={{ margin: '0', paddingLeft: '20px' }}>
                  {getCurrentTemplate().optionalColumns.map((col, index) => (
                    <li key={index}>
                      <strong>{col.name}</strong> - {col.desc}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Upload CSV File</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileUpload}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #404040',
                borderRadius: '8px',
                backgroundColor: '#1a1a1a',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}
            />
          </div>

          <div className="form-group">
            <label>Or Paste CSV Data</label>
            <textarea
              value={csvData}
              onChange={handleTextareaChange}
              placeholder="description,amount,date,category&#10;Grocery Shopping,45.67,2024-01-15,Food & Dining&#10;Gas Station,32.50,2024-01-14,Transportation"
              rows={8}
              style={{
                width: '100%',
                padding: '12px',
                border: '2px solid #404040',
                borderRadius: '8px',
                backgroundColor: '#1a1a1a',
                color: '#e0e0e0',
                fontFamily: 'monospace',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          {csvData && (
            <div style={{
              background: 'rgba(255, 107, 53, 0.1)',
              border: '1px solid #404040',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#f0f0f0', fontWeight: '600' }}>
                Preview: {csvData.split('\n').length - 1} rows detected
              </p>
              <div style={{
                fontSize: '12px',
                color: '#b0b0b0',
                fontFamily: 'monospace',
                maxHeight: '100px',
                overflow: 'auto'
              }}>
                {csvData.split('\n').slice(0, 4).map((line, index) => (
                  <div key={index} style={{ marginBottom: '2px' }}>
                    {index === 0 ? <strong>{line}</strong> : line}
                  </div>
                ))}
                {csvData.split('\n').length > 4 && (
                  <div style={{ color: '#888' }}>... and {csvData.split('\n').length - 4} more rows</div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            <button
              type="submit"
              className="btn"
              style={{ flex: 1 }}
              disabled={loading || !csvData.trim()}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid transparent',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></span>
                  Importing...
                </span>
              ) : (
                '📤 Import Expenses'
              )}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="btn"
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #666, #555)',
                boxShadow: '0 2px 8px rgba(102, 102, 102, 0.3)'
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ImportCSV;